/**
 * Building blocks of the Settings dialog, drawn after the native Settings
 * (apps/swift Views/Settings/SettingsView.swift):
 *
 * - Desktop (macOS window): every row is its own card on `sidebar`, radius 14,
 *   section titles 13 semibold in `ink`.
 * - Mobile (iOS sheet): an inset-grouped card per section with hairline
 *   separators, section titles 12 semibold in `muted`.
 */
import { createContext, useContext } from "react";
import { Button } from "@/components/ui/Button";
import { Icon, type IconId } from "@/components/ui/Icon";
import { cn } from "@/lib/utils";

const CompactContext = createContext(false);
export const SettingsCompactProvider = CompactContext.Provider;
export const useSettingsCompact = () => useContext(CompactContext);

/** The card surface every Settings block sits on: a desktop row, a phone group, the Pro card. */
export const settingsSurface = (compact: boolean) =>
  cn("bg-sidebar", compact ? "rounded-2xl" : "rounded-[14px]");

/** Rows inside one shared card (phones only: on desktop every row is a card). */
const GroupedContext = createContext(false);

/** Section title: 13 semibold ink on desktop, 12 semibold muted on phones. */
export function SettingsSectionTitle({
  children,
  onClick,
}: {
  children: React.ReactNode;
  /** For a hidden gesture: no cursor, no role, nothing drawn. */
  onClick?: () => void;
}) {
  const compact = useSettingsCompact();
  return (
    <h3
      onClick={onClick}
      className={cn(
        "font-semibold",
        // Desktop: one title style for every pane (native's MacSettingsSection).
        compact ? "px-4 text-[12px] text-muted" : "text-[13px] text-ink",
      )}
    >
      {children}
    </h3>
  );
}

export function SettingsSection({
  title,
  footer,
  onTitleClick,
  children,
}: {
  title?: React.ReactNode;
  footer?: React.ReactNode;
  /** A click on the title, for a hidden gesture. */
  onTitleClick?: () => void;
  children: React.ReactNode;
}) {
  const compact = useSettingsCompact();
  const grouped = compact;
  return (
    <section className="flex flex-col gap-2.5">
      {title && (
        <SettingsSectionTitle onClick={onTitleClick}>
          {title}
        </SettingsSectionTitle>
      )}
      <div
        className={cn(
          "flex flex-col",
          grouped
            ? cn(
                settingsSurface(true),
                // iOS insets the separators to the text, past the icon if any.
                "overflow-hidden [&>*+*]:relative [&>*+*]:before:absolute [&>*+*]:before:left-4 [&>*+*]:before:right-0 [&>*+*]:before:top-0 [&>*+*]:before:h-px [&>*+*]:before:bg-border-light",
                "[&>*+*:has(>svg:first-child)]:before:left-12",
              )
            : "gap-2.5",
        )}
      >
        <GroupedContext.Provider value={grouped}>{children}</GroupedContext.Provider>
      </div>
      {footer && <p className="px-4 text-[12px] text-muted">{footer}</p>}
    </section>
  );
}

type Accessory = "chevron" | "external" | "none";

export function SettingsRow({
  icon,
  label,
  subtitle,
  value,
  accessory = "chevron",
  danger,
  disabled,
  onClick,
  trailing,
  leading,
  className: extraClassName,
}: {
  className?: string;
  icon?: IconId;
  label: React.ReactNode;
  subtitle?: React.ReactNode;
  value?: React.ReactNode;
  accessory?: Accessory;
  danger?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** Replaces the accessory, e.g. a toggle or a select. */
  trailing?: React.ReactNode;
  /** Replaces the icon, e.g. an avatar. */
  leading?: React.ReactNode;
}) {
  const compact = useSettingsCompact();
  const grouped = useContext(GroupedContext);
  const body = (
    <>
      {leading ??
        (icon && (
          <Icon
            name={icon}
            size={compact ? 19 : 17}
            className={cn("shrink-0", danger ? "text-danger" : "text-ink")}
          />
        ))}
      <span className="flex min-w-0 flex-1 flex-col text-left">
        <span
          className={cn(
            // A described setting wraps its name, as SwiftUI's labels do; a
            // name alone (a person, a device) stays on one line.
            subtitle ? "break-words" : "truncate",
            compact ? "text-[16px]" : "text-[14px]",
            danger ? "text-danger" : "text-ink",
          )}
        >
          {label}
        </span>
        {subtitle && (
          <span className="text-[12px] leading-snug text-muted">{subtitle}</span>
        )}
      </span>
      {value && <span className="shrink-0 text-[14px] text-muted">{value}</span>}
      {trailing ??
        (accessory === "chevron" ? (
          <Icon name="chevron-right" size={12} className="shrink-0 text-muted" />
        ) : accessory === "external" ? (
          <Icon name="arrow-up-right" size={12} className="shrink-0 text-muted" />
        ) : null)}
    </>
  );
  const className = cn(
    "flex w-full items-center gap-3 font-chrome",
    compact
      ? "min-h-[52px] px-4 py-2.5"
      : cn("min-h-[44px] px-3.5 py-2.5", !grouped && settingsSurface(false)),
    disabled && "opacity-50",
    extraClassName,
  );
  if (!onClick || disabled) return <div className={className}>{body}</div>;
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        className,
        "h-auto justify-start border-0 text-left font-normal hover:bg-hover active:bg-active",
        grouped && "rounded-none",
      )}
    >
      {body}
    </Button>
  );
}

/** The native switch: accent track when on, a canvas knob. */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (on: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <Button
      variant="ghost"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      className={cn(
        // A disabled row already dims everything in it, the switch included.
        "h-[26px] w-[46px] justify-start border-0 p-0 disabled:opacity-100",
        checked ? "bg-accent hover:bg-accent-hover" : "bg-border hover:bg-border",
      )}
    >
      <span
        className={cn(
          "h-[22px] w-[22px] rounded-full bg-canvas shadow-sm transition-transform",
          checked ? "translate-x-[22px]" : "translate-x-[2px]",
        )}
      />
    </Button>
  );
}

/** A toggle row: the whole row flips it. */
export function SettingsToggleRow({
  icon,
  label,
  subtitle,
  checked,
  onChange,
  badge,
  disabled,
  className,
}: {
  className?: string;
  icon?: IconId;
  label: string;
  subtitle?: string;
  checked: boolean;
  onChange: (on: boolean) => void;
  badge?: string;
  disabled?: boolean;
}) {
  return (
    <SettingsRow
      icon={icon}
      label={
        badge ? (
          // Wraps on a phone rather than clipping the badge.
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {label}
            <span className="shrink-0 rounded-full bg-accent-bg px-1.5 py-px text-[10px] font-medium text-accent-text">
              {badge}
            </span>
          </span>
        ) : (
          label
        )
      }
      subtitle={subtitle}
      className={className}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      trailing={<Toggle checked={checked} onChange={onChange} label={label} disabled={disabled} />}
    />
  );
}

/** A page's placeholder while it loads (spinner) or when its list is empty. */
export function SettingsEmpty({
  icon,
  title,
  hint,
  loading,
}: {
  icon?: IconId;
  title?: React.ReactNode;
  hint?: string;
  loading?: boolean;
}) {
  return (
    // A spinner centres in the page (the page column fills the panel).
    <div className={cn("flex flex-col items-center gap-1 py-12 text-center text-muted", loading && "flex-1 justify-center")}>
      {loading ? (
        <Icon name="loader" className="animate-spin" />
      ) : (
        <>
          {icon && <Icon name={icon} size={24} className="mb-1 opacity-40" />}
          {title && <p className="text-[13px]">{title}</p>}
          {hint && <p className="text-[12px]">{hint}</p>}
        </>
      )}
    </div>
  );
}
